import { useState } from "react";
import { MapPin, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { detailMsg } from "@/lib/geo";
import { FormDialog } from "@/components/kids/kidsUi";
import { Field } from "@/components/common";
import { LocMap } from "./LocMap";

const cls = "q-input min-h-[44px] w-full";
const NOT_FOUND = "We couldn't find that address. Drop a pin on the map instead.";

function PlaceDialog({ hub, place, onClose, onSaved }) {
  const [f, setF] = useState(place || { name: "", emoji: "📍", address: "", radius: 200, lat: null, lng: null });
  const [miss, setMiss] = useState(false);
  const [busy, setBusy] = useState(false);
  const find = async () => {
    setBusy(true); setMiss(false);
    try { const { data } = await api.post(`/organizations/${hub.id}/places/geocode`, { address: f.address }); setF({ ...f, lat: data.lat, lng: data.lng }); }
    catch (e) { setMiss(true); toast.error(detailMsg(e, NOT_FOUND)); }
    setBusy(false);
  };
  const save = async () => {
    setBusy(true);
    const body = { name: f.name, emoji: f.emoji, address: f.address, radius: Number(f.radius), lat: f.lat, lng: f.lng };
    try { place ? await api.patch(`/organizations/${hub.id}/places/${place.id}`, body) : await api.post(`/organizations/${hub.id}/places`, body); toast.success("Place saved"); onSaved(); }
    catch (e) { const m = detailMsg(e, "Couldn't save"); if (m === NOT_FOUND) setMiss(true); toast.error(m); setBusy(false); }
  };
  return (
    <FormDialog open onClose={onClose} title={place ? "Edit place" : "Add a place"} testid="place-dialog">
      <div className="space-y-3">
        <div className="grid grid-cols-[4rem_1fr] gap-2">
          <Field label="Emoji"><input className={`${cls} text-center text-xl`} maxLength={8} value={f.emoji} onChange={(e) => setF({ ...f, emoji: e.target.value })} data-testid="place-emoji" /></Field>
          <Field label="Name"><input className={cls} maxLength={40} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Library" data-testid="place-name" /></Field>
        </div>
        <Field label="Address"><div className="flex gap-2">
          <input className={cls} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value, lat: null, lng: null })} placeholder="Street, city" data-testid="place-address" />
          <button className="q-btn q-btn-secondary min-h-[44px] shrink-0" disabled={busy || !f.address.trim()} onClick={find} data-testid="place-find"><Search className="h-4 w-4" /> Find</button></div></Field>
        {miss && <p className="text-sm font-semibold text-amber-300" data-testid="place-not-found">{NOT_FOUND}</p>}
        <LocMap lat={f.lat} lng={f.lng} radius={Number(f.radius)} height={200} onPick={(p) => { setF({ ...f, ...p }); setMiss(false); }} testid="place-map" />
        <p className="text-xs text-q-muted">Tap the map to drop or move the pin.</p>
        <Field label={`Radius: ${f.radius} m`}><input type="range" min={100} max={1000} step={50} value={f.radius} onChange={(e) => setF({ ...f, radius: e.target.value })} className="min-h-[44px] w-full" data-testid="place-radius" /></Field>
        <button className="q-btn q-btn-primary min-h-[48px] w-full" disabled={busy || !f.name.trim() || f.lat == null} onClick={save} data-testid="place-save">Save place</button>
      </div>
    </FormDialog>
  );
}

export function PlacesSection({ hub, places, canEdit, onPlace, onChanged }) {
  const [edit, setEdit] = useState(null);
  const del = (p) => api.delete(`/organizations/${hub.id}/places/${p.id}`).then(onChanged).catch((e) => toast.error(detailMsg(e, "Couldn't delete")));
  return (
    <section className="q-card mb-6 p-4" data-testid="places-section">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="q-label flex items-center gap-2"><MapPin className="h-4 w-4" /> Saved places</p>
        {canEdit && <button className="q-btn q-btn-ghost min-h-[44px]" onClick={() => setEdit({})} data-testid="add-place-btn"><Plus className="h-4 w-4" /> Add place</button>}
      </div>
      {!places.length ? <p className="text-sm text-q-muted">No saved places yet.</p> : (
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">{places.map((p) => (
          <li key={p.id} className="flex items-center gap-2" data-testid={`place-${p.id}`}>
            <button className="flex min-h-[48px] min-w-0 flex-1 items-center gap-2 rounded-2xl border border-q-line bg-q-raised px-3 text-left font-semibold text-q-text active:scale-95" onClick={() => onPlace(p)} data-testid={`place-checkin-${p.id}`}>
              <span className="text-xl">{p.emoji}</span><span className="truncate">I'm at {p.name}</span></button>
            {canEdit && <button className="q-btn q-btn-ghost min-h-[44px] min-w-[44px]" aria-label={`Edit ${p.name}`} onClick={() => setEdit(p)} data-testid={`place-edit-${p.id}`}><MapPin className="h-4 w-4" /></button>}
            {canEdit && <button className="q-btn q-btn-ghost min-h-[44px] min-w-[44px]" aria-label={`Delete ${p.name}`} onClick={() => del(p)} data-testid={`place-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>}
          </li>))}</ul>)}
      {edit && <PlaceDialog hub={hub} place={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); onChanged(); }} />}
    </section>
  );
}
